import React from "react";
import { Link } from "react-router-dom";
import { Star, TrendUp, TrendDown, PushPin, PushPinSlash } from "@phosphor-icons/react";
import { Sparkline } from "./Sparkline";
import { PlayerAvatar } from "./PlayerAvatar";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const TIER_CLASS = {
  PRIME: "chip-lock",
  STRONG: "chip-strong",
  LEAN: "chip-lean",
};

export function PropCard({ prop, onToggleWatch, isWatched, index, onCreatorPinChange }) {
  const player = prop.player || {};
  const pickIsOver = prop.pick === "OVER";
  const TrendIcon = pickIsOver ? TrendUp : TrendDown;
  const accent = pickIsOver ? "var(--over)" : "var(--under)";
  const { user } = useAuth();
  const isAdmin = !!(user && user.is_admin);

  const toggleCreatorPin = async () => {
    try {
      const action = prop.creator_pinned ? "unpin" : "pin";
      await api.post(`/admin/${action}/${prop.id}`);
      toast(prop.creator_pinned ? "Creator pin removed" : "Creator's Pick added", {
        description: `${player.name} — ${prop.stat_type} ${prop.pick} ${prop.line}`,
      });
      if (onCreatorPinChange) onCreatorPinChange();
    } catch (e) {
      toast.error("Pin action failed", { description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <article
      className="prop-card bg-[#121212] border border-[#27272A] p-5 grid"
      style={{ gridTemplateColumns: "1fr", gap: "16px" }}
      data-testid={`prop-card-${prop.id}`}
    >
      {/* Header row */}
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <PlayerAvatar player={player} size={44} />
          <div className="min-w-0">
            <Link
              to={`/player/${player.id}`}
              className="block font-head text-base text-white truncate hover:text-[#FF4500] transition-colors"
              data-testid={`prop-player-link-${prop.id}`}
            >
              {player.name}
            </Link>
            <div className="overline mt-0.5 truncate flex items-center gap-1.5">
              <span>{player.sport} • {player.team_abbr} {player.is_home ? "vs" : "@"} {player.opponent_abbr} • {player.position}</span>
              {prop.source === "live" && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 ml-1"
                  style={{ background: "rgba(255,69,0,0.15)", color: "#FF4500", fontSize: 9 }}
                  data-testid={`prop-live-${prop.id}`}
                  title={prop.bookmaker_title ? `Live: ${prop.bookmaker_title}` : "Live odds"}
                >
                  <span style={{ width: 5, height: 5, background: "#FF4500", borderRadius: 999 }} />
                  LIVE
                </span>
              )}
              {prop.creator_pinned && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 ml-1"
                  style={{ background: "rgba(255,69,0,0.20)", color: "#FF4500", fontSize: 9, fontWeight: 700 }}
                  data-testid={`prop-creator-pinned-${prop.id}`}
                  title="Creator's Pick — hand-selected by GP"
                >
                  ★ CREATOR
                </span>
              )}
              {prop.pinned && !prop.creator_pinned && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5"
                  style={{ background: "rgba(250,204,21,0.15)", color: "#FACC15", fontSize: 9 }}
                  data-testid={`prop-pinned-${prop.id}`}
                  title="Top pick — locked in until the game starts"
                >
                  📌 PINNED
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {isAdmin && (
            <button
              onClick={toggleCreatorPin}
              aria-label={prop.creator_pinned ? "Remove Creator's Pick" : "Make Creator's Pick"}
              title={prop.creator_pinned ? "Remove Creator's Pick" : "Pin as Creator's Pick"}
              className={`flex items-center gap-1 px-2 py-1 border transition-colors ${
                prop.creator_pinned
                  ? "border-[#FF4500] text-[#FF4500]"
                  : "border-[#27272A] text-[#A1A1AA] hover:border-[#FF4500] hover:text-[#FF4500]"
              }`}
              data-testid={`prop-admin-pin-${prop.id}`}
            >
              {prop.creator_pinned ? <PushPinSlash size={14} weight="fill" /> : <PushPin size={14} weight="bold" />}
              <span className="overline" style={{ color: "inherit" }}>{prop.creator_pinned ? "Unpin" : "Pin"}</span>
            </button>
          )}
          <button
            onClick={() => onToggleWatch(prop.id)}
            aria-label={isWatched ? "Remove from My Picks" : "Add to My Picks"}
            title={isWatched ? "In your picks" : "Add to My Picks"}
            className={`flex items-center gap-1.5 px-2.5 py-1 border transition-colors ${
              isWatched
                ? "border-[#FACC15] text-[#FACC15]"
                : "border-[#27272A] text-[#A1A1AA] hover:border-white/30 hover:text-white"
            }`}
            data-testid={`prop-watch-${prop.id}`}
          >
            <Star size={14} weight={isWatched ? "fill" : "regular"} />
            <span className="overline" style={{ color: "inherit" }}>{isWatched ? "Picked" : "Add"}</span>
          </button>
        </div>
      </header>

      {/* Stat / pick row */}
      <div className="grid grid-cols-12 gap-4 items-end">
        <div className="col-span-5">
          <div className="overline">Stat</div>
          <div className="font-head text-white text-lg leading-tight mt-1 uppercase">{prop.stat_type}</div>
        </div>
        <div className="col-span-3 text-right">
          <div className="overline">Line</div>
          <div className="big-num text-white text-3xl leading-none mt-1">{prop.line}</div>
        </div>
        <div className="col-span-4 text-right">
          <div className="overline">Pick</div>
          <div
            className={`inline-flex items-center gap-1 mt-1 px-3 py-1.5 font-head text-base uppercase ${pickIsOver ? "pick-over" : "pick-under"}`}
            data-testid={`prop-pick-${prop.id}`}
          >
            <TrendIcon size={16} weight="bold" />
            {prop.pick}
          </div>
        </div>
      </div>

      {/* Sparkline */}
      <div className="border-t border-[#27272A] pt-3">
        <div className="flex items-center justify-between mb-1">
          <span className="overline">Last 10 Games</span>
          <span className="font-data text-xs text-white">
            <span style={{ color: accent }}>{prop.hit_count}</span>
            <span className="text-[#52525b]">/{prop.hit_total} hit</span>
          </span>
        </div>
        <Sparkline data={prop.last_10} line={prop.line} pick={prop.pick} />
      </div>

      {/* Footer row */}
      <footer className="flex items-center justify-between border-t border-[#27272A] pt-3">
        <div className="flex items-center gap-4">
          <div>
            <div className="overline">L5 Avg</div>
            <div className="font-data text-white text-sm">{prop.last_5_avg}</div>
          </div>
          <div>
            <div className="overline">Season</div>
            <div className="font-data text-[#A1A1AA] text-sm">{prop.season_avg}</div>
          </div>
          {prop.matchup_delta !== undefined && prop.matchup_delta !== 0 && (
            <div title={prop.matchup_text} data-testid={`prop-matchup-${prop.id}`}>
              <div className="overline">Matchup</div>
              <div
                className="font-data text-sm flex items-center gap-0.5"
                style={{ color: prop.matchup_delta > 0 ? "#22c55e" : "#ef4444" }}
              >
                {prop.matchup_delta > 0 ? "+" : ""}{prop.matchup_delta}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2.5 py-1 text-xs uppercase font-head tracking-wider ${TIER_CLASS[prop.tier]}`} data-testid={`prop-tier-${prop.id}`}>
            {prop.tier}
          </span>
          <span className="big-num text-white text-xl" data-testid={`prop-confidence-${prop.id}`}>
            {prop.confidence}<span className="text-[#52525b] text-sm">%</span>
          </span>
        </div>
      </footer>
    </article>
  );
}
